/**
 * IO Service Orders — admin inbound queue (Mantine).
 *
 * Inbox for every order placed against `platformServices` across
 * every studio. Workflow:
 *   new → contacted → in_progress → completed
 *   (or cancelled)
 *
 * Each status change appends to `statusHistory[]` for an audit
 * trail. Click-to-call + click-to-email on the contact card.
 */
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconClock,
  IconMail,
  IconMapPin,
  IconPhone,
  IconUser,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';

import { auth } from '@/lib/firebase';
import {
  PLATFORM_SERVICE_ORDER_STATUSES,
  deletePlatformServiceOrder,
  getPlatformServiceOrderStatusConfig,
  updatePlatformServiceOrderStatus,
  type PlatformServiceOrder,
  type PlatformServiceOrderStatus,
} from '@/lib/platformServiceOrders';
import { getPlatformServiceCategoryConfig } from '@/lib/platformServices';
import { usePlatformServiceOrders } from '@/lib/usePlatformServiceOrders';

export function IOOrdersAdmin() {
  const [statusFilter, setStatusFilter] = useState<PlatformServiceOrderStatus | 'all'>(
    'all',
  );
  const [search, setSearch] = useState('');
  const [openOrder, setOpenOrder] = useState<PlatformServiceOrder | null>(null);

  const { data, loading } = usePlatformServiceOrders({
    scope: 'admin',
    status: statusFilter === 'all' ? null : statusFilter,
    searchText: search,
  });

  const counts = useMemo(() => {
    const c: Record<PlatformServiceOrderStatus, number> = {
      new: 0,
      contacted: 0,
      in_progress: 0,
      completed: 0,
      cancelled: 0,
    };
    for (const o of data) c[o.status] = (c[o.status] ?? 0) + 1;
    return c;
  }, [data]);

  return (
    <Stack gap="md">
      <div>
        <Title order={3}>IO Service orders</Title>
        <Text size="sm" c="dimmed">
          {data.length} order{data.length === 1 ? '' : 's'} across all studios
        </Text>
      </div>

      <Group>
        <Select
          data={[
            { value: 'all', label: `All (${data.length})` },
            ...PLATFORM_SERVICE_ORDER_STATUSES.map((s) => ({
              value: s.key,
              label: `${s.label} (${counts[s.key] ?? 0})`,
            })),
          ]}
          value={statusFilter}
          onChange={(v) => setStatusFilter((v as PlatformServiceOrderStatus | 'all') ?? 'all')}
          w={240}
        />
        <TextInput
          placeholder="Search by service, studio, contact name…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          flex={1}
        />
      </Group>

      {loading ? (
        <Text c="dimmed">Loading…</Text>
      ) : data.length === 0 ? (
        <Alert icon={<IconAlertCircle size={16} />} color="gray">
          No orders match your filters.
        </Alert>
      ) : (
        <Stack gap={8}>
          {data.map((o) => (
            <AdminOrderRow key={o.id} order={o} onOpen={() => setOpenOrder(o)} />
          ))}
        </Stack>
      )}

      {openOrder ? (
        <OrderDetailModal order={openOrder} onClose={() => setOpenOrder(null)} />
      ) : null}
    </Stack>
  );
}

// ───── Row ────────────────────────────────────────────────────

function AdminOrderRow({
  order,
  onOpen,
}: {
  order: PlatformServiceOrder;
  onOpen: () => void;
}) {
  const catCfg = getPlatformServiceCategoryConfig(order.serviceCategory);
  const stCfg = getPlatformServiceOrderStatusConfig(order.status);
  const at = order.createdAt?.toMillis?.() ?? 0;
  const submittedLabel = at
    ? new Date(at).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

  return (
    <Card
      withBorder
      p="sm"
      style={{ cursor: 'pointer' }}
      onClick={onOpen}
      role="button"
    >
      <Group wrap="nowrap" gap="md" align="flex-start">
        <Badge color="gray" variant="light" style={{ background: catCfg.tintBg, color: catCfg.color }}>
          {catCfg.label}
        </Badge>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} size="sm">{order.serviceTitle}</Text>
          <Text size="xs" c="dimmed" mt={2}>
            {order.orgName} · {order.contactName} · {order.contactPhone}
            {order.projectName ? ` · ${order.projectName}` : ''}
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            {submittedLabel}
          </Text>
        </div>
        <Badge style={{ background: stCfg.tintBg, color: stCfg.color }}>
          {stCfg.label}
        </Badge>
      </Group>
    </Card>
  );
}

// ───── Detail modal ──────────────────────────────────────────

function OrderDetailModal({
  order,
  onClose,
}: {
  order: PlatformServiceOrder;
  onClose: () => void;
}) {
  const user = auth.currentUser;
  const [status, setStatus] = useState<PlatformServiceOrderStatus>(order.status);
  const [adminNotes, setAdminNotes] = useState(order.adminNotes ?? '');
  const [quotedPrice, setQuotedPrice] = useState<number | ''>(
    order.quotedPrice ?? '',
  );
  const [statusNote, setStatusNote] = useState('');
  const [busy, setBusy] = useState(false);

  const stCfg = getPlatformServiceOrderStatusConfig(order.status);

  async function save() {
    if (!user) return;
    setBusy(true);
    try {
      await updatePlatformServiceOrderStatus(
        order.id,
        status,
        { uid: user.uid, name: user.displayName ?? user.email ?? 'Admin' },
        {
          note: statusNote || undefined,
          adminNotes,
          quotedPrice: typeof quotedPrice === 'number' ? quotedPrice : undefined,
        },
      );
      notifications.show({
        color: 'green',
        title: 'Order updated',
        message: order.serviceTitle,
      });
      onClose();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm('Delete this order? This cannot be undone.')) return;
    setBusy(true);
    try {
      await deletePlatformServiceOrder(order.id);
      notifications.show({ color: 'green', title: 'Order deleted', message: '' });
      onClose();
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Delete failed',
        message: e instanceof Error ? e.message : 'Unknown error',
      });
      setBusy(false);
    }
  }

  return (
    <Modal
      opened
      onClose={() => !busy && onClose()}
      size="lg"
      title={
        <Group gap="xs">
          <Text fw={600}>{order.serviceTitle}</Text>
          <Badge style={{ background: stCfg.tintBg, color: stCfg.color }}>
            {stCfg.label}
          </Badge>
        </Group>
      }
    >
      <Stack gap="md">
        {/* Contact card */}
        <Card withBorder p="sm">
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb="xs">
            Studio contact — {order.orgName}
          </Text>
          <Stack gap={4}>
            <Group gap={6}>
              <IconUser size={14} />
              <Text size="sm" fw={500}>{order.contactName}</Text>
            </Group>
            <Group gap={6}>
              <IconPhone size={14} />
              <a href={`tel:${order.contactPhone}`}>
                <Text size="sm" c="blue">{order.contactPhone}</Text>
              </a>
            </Group>
            {order.contactEmail ? (
              <Group gap={6}>
                <IconMail size={14} />
                <a href={`mailto:${order.contactEmail}`}>
                  <Text size="sm" c="blue">{order.contactEmail}</Text>
                </a>
              </Group>
            ) : null}
            {order.serviceAddress ? (
              <Group gap={6} wrap="nowrap" align="flex-start">
                <IconMapPin size={14} style={{ marginTop: 3 }} />
                <Text size="sm" c="dimmed">{order.serviceAddress}</Text>
              </Group>
            ) : null}
            {order.preferredDate ? (
              <Group gap={6}>
                <IconClock size={14} />
                <Text size="sm" c="dimmed">Preferred: {order.preferredDate}</Text>
              </Group>
            ) : null}
            {order.projectName ? (
              <Text size="sm" c="dimmed">Project: {order.projectName}</Text>
            ) : null}
            {order.notes ? (
              <Alert color="gray" mt={6}>
                <Text size="sm">{order.notes}</Text>
              </Alert>
            ) : null}
          </Stack>
        </Card>

        {/* Update status */}
        <Card withBorder p="sm">
          <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb="xs">
            Update status
          </Text>
          <Stack gap="sm">
            <Select
              label="Status"
              data={PLATFORM_SERVICE_ORDER_STATUSES.map((s) => ({
                value: s.key,
                label: s.label,
              }))}
              value={status}
              onChange={(v) =>
                setStatus((v as PlatformServiceOrderStatus) ?? 'new')
              }
            />
            <NumberInput
              label="Quoted price (₹)"
              value={quotedPrice}
              onChange={(v) =>
                setQuotedPrice(typeof v === 'number' ? v : v ? Number(v) : '')
              }
              placeholder="Leave empty to clear"
              hideControls
            />
            <Textarea
              label="Note from InteriorIO (visible to studio)"
              autosize
              minRows={2}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.currentTarget.value)}
              placeholder="Optional message shown on the studio's My requests list."
            />
            <TextInput
              label="Internal status note"
              value={statusNote}
              onChange={(e) => setStatusNote(e.currentTarget.value)}
              placeholder="Why this status change? (kept in audit trail)"
            />
          </Stack>
        </Card>

        {/* Timeline */}
        {order.statusHistory && order.statusHistory.length > 0 ? (
          <Card withBorder p="sm">
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb="xs">
              Timeline
            </Text>
            <Stack gap={8}>
              {[...order.statusHistory]
                .sort((a, b) => b.at - a.at)
                .map((ev, i) => {
                  const sc = getPlatformServiceOrderStatusConfig(ev.status);
                  return (
                    <Group key={`${ev.at}-${i}`} align="flex-start" gap="sm" wrap="nowrap">
                      <Badge style={{ background: sc.tintBg, color: sc.color }}>
                        {sc.label}
                      </Badge>
                      <div style={{ flex: 1 }}>
                        <Text size="xs">
                          {new Date(ev.at).toLocaleString('en-IN', {
                            day: 'numeric',
                            month: 'short',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                          {ev.byName ? ` · ${ev.byName}` : ''}
                        </Text>
                        {ev.note ? (
                          <Text size="xs" c="dimmed">{ev.note}</Text>
                        ) : null}
                      </div>
                    </Group>
                  );
                })}
            </Stack>
          </Card>
        ) : null}

        <Group justify="space-between">
          <Button
            variant="default"
            color="red"
            onClick={remove}
            disabled={busy}
          >
            Delete order
          </Button>
          <Group gap="xs">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={save} loading={busy}>
              Save
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
